import {useCallback, useEffect, useRef, useState} from 'react';
import {apiFetch} from '../api';

export default function useChecklists({onError}) {
    const [loading, setLoading] = useState(true);
    const [checklistData, setChecklistData] = useState([]);
    const [initialGroups, setInitialGroups] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalTemplates, setModalTemplates] = useState([]);

    const dataRef = useRef(checklistData);
    useEffect(() => { dataRef.current = checklistData; }, [checklistData]);

    useEffect(() => {
        const controller = new AbortController();
        Promise.all([
            apiFetch('/checklists', {signal: controller.signal}).then(res => res.json()),
            apiFetch('/checklist-groups', {signal: controller.signal}).then(res => res.json()),
        ]).then(([checklists, grps]) => {
            setChecklistData(checklists);
            setInitialGroups(grps);
            setLoading(false);
        }).catch(err => {
            if (err.name !== 'AbortError') {
                onError(err);
                setLoading(false);
            }
        });
        return () => controller.abort();
    }, [onError]);

    const putChecklist = useCallback(async (checklistId, updated) => {
        try {
            const res = await apiFetch(`/checklists/${checklistId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(updated),
            });
            const saved = await res.json();
            setChecklistData(prev => prev.map(c => c.id === checklistId ? saved : c));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const deleteChecklist = useCallback(async (id) => {
        try {
            await apiFetch(`/checklists/${id}`, {method: 'DELETE'});
            setChecklistData(prev => prev.filter(c => c.id !== id));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const updateTitle = useCallback((checklistId, title) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        putChecklist(checklistId, {...checklist, title});
    }, [putChecklist]);

    const updateItem = useCallback(async (checklistId, itemId, title) => {
        try {
            const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({title}),
            });
            const updated = await res.json();
            setChecklistData(prev => prev.map(c =>
                c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
            ));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const deleteItem = useCallback(async (checklistId, itemId) => {
        try {
            await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {method: 'DELETE'});
            setChecklistData(prev => prev.map(c =>
                c.id === checklistId ? {...c, items: c.items.filter(i => i.id !== itemId)} : c
            ));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const addItem = useCallback(async (checklistId, title) => {
        try {
            const res = await apiFetch(`/checklists/${checklistId}/items`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({title}),
            });
            const newItem = await res.json();
            setChecklistData(prev => prev.map(c =>
                c.id === checklistId ? {...c, items: [newItem, ...c.items]} : c
            ));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const starItem = useCallback(async (checklistId, itemId) => {
        try {
            const checklist = dataRef.current.find(c => c.id === checklistId);
            const item = checklist.items.find(i => i.id === itemId);
            const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({starred: !item.starred}),
            });
            const updated = await res.json();
            setChecklistData(prev => prev.map(c =>
                c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
            ));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const setDueDate = useCallback(async (checklistId, itemId, dueDate) => {
        try {
            const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({dueDate}),
            });
            const updated = await res.json();
            setChecklistData(prev => prev.map(c =>
                c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
            ));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const toggleItem = useCallback(async (checklistId, itemId) => {
        try {
            const checklist = dataRef.current.find(c => c.id === checklistId);
            const item = checklist.items.find(i => i.id === itemId);
            const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({checked: !item.checked}),
            });
            const updated = await res.json();
            setChecklistData(prev => prev.map(c =>
                c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
            ));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const reorderItems = useCallback((checklistId, movedItemId, afterId, newItems) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const updated = {...checklist, items: newItems, sortMode: 'custom'};
        setChecklistData(prev => prev.map(c => c.id === checklistId ? updated : c));
        apiFetch(`/checklists/${checklistId}/items/${movedItemId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({after: afterId}),
        }).catch(err => onError(err));
        if (checklist.sortMode !== 'custom') {
            apiFetch(`/checklists/${checklistId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({...checklist, sortMode: 'custom'}),
            }).catch(err => onError(err));
        }
    }, [onError]);

    const reorderChecklists = useCallback((movedId, afterId, reorderedGroup) => {
        setChecklistData(prev => {
            const groupIds = new Set(reorderedGroup.map(c => c.id));
            const result = [];
            let ri = 0;
            for (const c of prev) {
                result.push(groupIds.has(c.id) ? reorderedGroup[ri++] : c);
            }
            return result;
        });
        apiFetch(`/checklists/${movedId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({after: afterId, reorder: true}),
        }).catch(err => onError(err));
    }, [onError]);

    const toggleSinkChecked = useCallback((checklistId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const newVal = checklist.sinkCheckedItems === false;
        putChecklist(checklistId, {...checklist, sinkCheckedItems: newVal});
    }, [putChecklist]);

    const toggleHideChecked = useCallback((checklistId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const newVal = !checklist.hideCheckedItems;
        putChecklist(checklistId, {...checklist, hideCheckedItems: newVal});
    }, [putChecklist]);

    const toggleSortMode = useCallback((checklistId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        if (checklist.sortMode === 'custom') {
            putChecklist(checklistId, {...checklist, sortMode: 'created_at'});
        } else {
            const sorted = [...checklist.items].sort((a, b) => a.position - b.position);
            putChecklist(checklistId, {...checklist, items: sorted, sortMode: 'custom'});
        }
    }, [putChecklist]);

    const addChecklist = useCallback(async (title, groupId) => {
        try {
            const body = {title, items: []};
            if (groupId) body.groupId = groupId;
            const res = await apiFetch('/checklists', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(body),
            });
            const created = await res.json();
            setChecklistData(prev => [...prev, created]);
            return created;
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const openNewListModal = useCallback(async () => {
        try {
            const res = await apiFetch('/checklist-templates');
            const templates = await res.json();
            setModalTemplates(templates);
            setShowModal(true);
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const spawnFromTemplate = useCallback(async (title, templateId, groupId) => {
        try {
            const res = await apiFetch(`/checklist-templates/${templateId}/spawn`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({name: title, groupId}),
            });
            const created = await res.json();
            setChecklistData(prev => [...prev, created]);
            return created;
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const closeModal = useCallback(() => setShowModal(false), []);

    const appendChecklist = useCallback((checklist) => {
        setChecklistData(prev => [...prev, checklist]);
    }, []);

    return {
        loading,
        checklistData,
        initialGroups,
        showModal,
        modalTemplates,
        deleteChecklist,
        updateTitle,
        updateItem,
        deleteItem,
        addItem,
        starItem,
        setDueDate,
        toggleItem,
        reorderItems,
        reorderChecklists,
        toggleSinkChecked,
        toggleHideChecked,
        toggleSortMode,
        addChecklist,
        spawnFromTemplate,
        openNewListModal,
        closeModal,
        appendChecklist,
    };
}
